/* HITUNG GALON — the gallon count at a customer (mockup 1 "PHONE 1" + mockup 2 "PHONE A"): one number, the
   reason when it differs, a stamped camera photo, and — when gallons are short — how the shortfall is settled
   (Tagih ganti rugi / still elsewhere / Tidak ditagih). The count applies at once; the server stamps who
   counted, when, and the balance it is compared against. The preview runs the SAME plan the server writes
   (DISTGL.planCount). Every write goes through the adaptor `api` the caller hands in (field: real server or
   practice copy; office: its own adaptor) — never the server directly.
   The bundle shares one scope across files, so every top-level name here is unique (Hg*, hg*, HG_*). */
const { useState: uShg, useEffect: uEhg, useRef: uRhg } = React;

// Chip / summary labels in DISTGL.COUNT_REASONS order (full keys, so the i18n scan reads them).
const HG_REASON_LABEL = { pecah: 'hg.r_pecah', dibawa: 'hg.r_dibawa', catatan_salah: 'hg.r_catatan_salah', belum_kembali: 'hg.r_belum_kembali', lainnya: 'hg.r_lainnya' };
const HG_PAY_LABEL = { tunai: 'hg.payTunai', bon: 'hg.payBon', transfer: 'hg.payTransfer', transfer_pending: 'hg.payTfWait', ganti_galon: 'hg.payGantiGalon' };
// Why Simpan is still locked — the hint above it (the server's copy for the plan's codes).
const HG_BLOCK_HINT = { REASON_REQUIRED: 'hg.needReason', SHORTFALL_REQUIRED: 'hg.needShortfall', OUTSIDE_REASON: 'hg.outsideOnly', NO_GR_CAP: 'hg.noCap', NO_PRICE: 'hg.noPrice', NEGATIVE: 'hg.badNumber' };
const hgReduced = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };
const hgRole = (r) => (r && window.FS && window.FS.roleName ? window.FS.roleName(r) : r || '');
const hgWho = (name, role) => [name, role].filter(Boolean).join(' · ');
// "27 Okt 2026, 14:32" (the phone's clock)
const hgWhen = (v) => {
  const d = new Date(v); if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  let day; try { day = d.toLocaleDateString(trFl('fld.locale'), { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { day = d.toISOString().slice(0, 10); }
  return day + ', ' + p(d.getHours()) + ':' + p(d.getMinutes());
};

// The big number rolls up (more) or down (less) with a blur (mockup 1): the input rolls in while the old
// value leaves as a ghost. Restarted on every change the way the mockup does (class off → reflow → on).
function useHgRoll(value) {
  const wrap = uRhg(null); const prev = uRhg(value);
  const [ghost, setGhost] = uShg(null);
  uEhg(() => {
    const from = prev.current; prev.current = value;
    if (from === value || hgReduced()) return undefined;
    const inp = wrap.current && wrap.current.querySelector('input.mlap-stepval');
    if (!inp) return undefined;
    const dir = value > from ? 'mlap-hg-up' : 'mlap-hg-down';
    inp.classList.remove('mlap-hg-roll', 'mlap-hg-up', 'mlap-hg-down'); void inp.offsetWidth; inp.classList.add('mlap-hg-roll', dir);
    setGhost({ v: from, dir, k: Date.now(), left: inp.offsetLeft, top: inp.offsetTop, width: inp.offsetWidth, height: inp.offsetHeight });
    const t = setTimeout(() => setGhost(null), 300);
    return () => clearTimeout(t);
  }, [value]);
  return { wrap, ghost };
}
// Keeps a block mounted while it springs shut (mockup .reasonbox.hide), then drops it — so hidden chips leave the
// tab order once it is gone. Returns true while it must stay mounted.
function useHgPresence(open, ms) {
  const [kept, setKept] = uShg(open);
  uEhg(() => {
    if (open) { setKept(true); return undefined; }
    if (hgReduced()) { setKept(false); return undefined; }
    const t = setTimeout(() => setKept(false), ms);
    return () => clearTimeout(t);
  }, [open]);
  return open || kept;
}
// Two letters for the counter's avatar (mockup "KO" for Komang; "WS" for Wayan Sudarma).
const hgInitials = (name) => {
  const w = String(name || '').trim().split(/\s+/).filter(Boolean);
  return (w.length > 1 ? w[0].charAt(0) + w[1].charAt(0) : (w[0] || '').slice(0, 2)).toUpperCase();
};

// HITUNG GALON screen. `me` = { name, role } of the signed-in account (shown only; the server stamps the
// real counter). `canTagih` = holds distribusiInput. `price` = the owner's ganti rugi price per gallon.
// `pendingSale` — the lock while a sale to this customer is saved but its stop not marked yet (it fails CLOSED):
//   false = free · true = a sale is pending (counting waits) · 'checking' = still looking (Simpan stays off) ·
//   'error' = could not look (Simpan stays off; `onRecheck` retries). The office passes nothing (= free).
// `refs` (optional, the field's refStore) keeps the clientRef until saved; without it one ref per open.
// The record shown ("Menurut catatan") goes to the server as expectedBefore: a shortfall priced from a record
// that has since moved comes back 409 BEFORE_CHANGED with the current one, and the screen re-prices from it.
// `onDone(result)` when the success sheet closes; `onHistory(result)` (optional) adds "Lihat riwayat galon" to it.
function HgCount({ api, cust: c, me, canTagih, price, pendingSale, onRecheck, refs, onDone, onHistory, onBack }) {
  const [rec, setRec] = uShg(c.gallonsHeld == null ? 0 : c.gallonsHeld);
  const [stale, setStale] = uShg('');   // the server's BEFORE_CHANGED message, until the next Simpan
  const saleWait = pendingSale === true;
  const checking = pendingSale === 'checking';
  const checkErr = pendingSale === 'error';
  const [counted, setCounted] = uShg(rec);
  const [moved, setMoved] = uShg(false);
  const [reason, setReason] = uShg('');
  const [note, setNote] = uShg('');
  const [shortfall, setShortfall] = uShg(null);
  const [pay, setPay] = uShg('');
  const [photo, setPhoto] = uShg(null);
  const [flash, setFlash] = uShg(0);
  const [busy, setBusy] = uShg(false);
  const [err, setErr] = uShg('');
  const [saved, setSaved] = uShg(null);
  const slot = 'count:' + c.id;
  const [ref] = uShg(() => (refs ? refs.take(slot) : FIELDLOGIC.newRef()));   // a retried Simpan is never a second count
  const roll = useHgRoll(counted);
  const diff = counted - rec;
  const plan = DISTGL.planCount({ before: rec, counted, reason: diff ? reason : null, shortfall: diff < 0 ? shortfall : null, price, canTagih });
  const reasonRow = DISTGL.COUNT_REASONS.find((r) => r.key === reason) || null;
  // "masih di tempat lain" only for Dibawa / Belum dikembalikan: another reason drops that choice
  uEhg(() => { if (shortfall === 'di_luar' && !(reasonRow && reasonRow.outsideOk)) setShortfall(null); }, [reason]);
  const needPay = diff < 0 && shortfall === 'tagih' && !pay;
  // the one-time sheen when Simpan lights up (mockup)
  const lit = !pendingSale && !!photo && !plan.blocked && !needPay;
  const [sheen, setSheen] = uShg(0); const wasLit = uRhg(false);
  uEhg(() => { if (lit && !wasLit.current && !hgReduced()) setSheen((k) => k + 1); wasLit.current = lit; }, [lit]);

  const change = (v) => { setCounted(v); setMoved(true); };
  const takePhoto = (p) => setPhoto(p);
  // the camera flash fires the moment a picture comes back from the camera (the file input changes), before
  // the upload; the thumbnail pops in once the upload is done
  const shutter = (e) => { if (e.target && e.target.type === 'file' && e.target.files && e.target.files.length && !hgReduced()) setFlash(Date.now()); };
  const reasonOn = useHgPresence(diff !== 0, 450);
  const send = () => {
    setBusy(true); setErr(''); setStale('');
    api.countGallon(c.id, FIELDLOGIC.countBody({ counted, expectedBefore: rec, reasonKey: diff ? reason : '', shortfall: diff < 0 ? shortfall : null, payMethod: diff < 0 && shortfall === 'tagih' ? pay : null, note: diff ? note : '', photo, clientRef: ref }))
      .then((r) => { if (refs) refs.done(slot); setSaved(r || {}); })
      .catch((e) => {
        const eb = (e && e.body && e.body.error) || {};
        const now = eb.details && eb.details.before;
        // the record moved since the screen opened: show the current one, keep the count, photo and choices,
        // re-price, and let the counter press Simpan again (nothing was written)
        if (eb.code === 'BEFORE_CHANGED' && Number.isInteger(now) && now >= 0) { setRec(now); setStale(eb.message || fldErrMsg(e)); } else setErr(fldErrMsg(e));
      })
      .finally(() => setBusy(false));
  };
  const back = () => { if (!busy) onBack(); };   // Batal is inactive while the count is being saved
  const hint = saleWait || checkErr || saved ? ''
    : checking ? <span className="muted">{trFl('hg.checking')}</span>
    : !photo ? trFl('hg.needPhoto')
      : plan.blocked ? trFl(HG_BLOCK_HINT[plan.blocked] || 'hg.badNumber')
        : needPay ? trFl('hg.needPay')
          : <span className="ok">{trFl(diff === 0 ? 'hg.readySame' : 'hg.ready')}</span>;
  const opt = (on) => 'mlap-hg-opt' + (on ? ' on' : '');
  const who = hgWho(me && me.name, me && me.role);

  return (
    <div className={'mlap-screen mlap-hg-count' + (busy ? ' mlap-hg-busy' : '')}>
      <FldTop title={trFl('hg.title')} sub={c.armada || ''} onBack={back} />
      <div className="mlap-body">
        <FldCustHead name={c.name} sub={[c.code, c.address].filter(Boolean).join(' · ')} />
        {checkErr ? <FldNotice tone="warn" alert title={trFl('hg.checkErr')} sub={trFl('hg.checkErrSub')} action={onRecheck ? trFl('hg.retry') : null} onAction={onRecheck} /> : null}
        {stale ? <FldNotice tone="warn" alert title={stale} sub={trFl('hg.staleSub')} /> : null}
        {saleWait ? <FldNotice tone="warn" title={trFl('hg.waitSale')} sub={trFl('hg.waitSaleSub')} /> : (
          <>
            <div className="mlap-card">
              <div className="mlap-kv tall"><span>{trFl('hg.record')}</span><b className="big">{trFl('hg.nGalon', { n: rec })}</b></div>
              <div className="mlap-hg-counter" ref={roll.wrap}>
                <FldStepper cls="big mlap-hg-step" label={trFl('hg.counted')} hint={trFl('hg.countedHint')} value={counted} onChange={change} min={0} max={9999} unit={trFl('hg.gkUnit')} />
                {roll.ghost ? <span key={roll.ghost.k} className={'mlap-hg-ghost ' + roll.ghost.dir} aria-hidden="true" style={{ left: roll.ghost.left, top: roll.ghost.top, width: roll.ghost.width, height: roll.ghost.height }}>{roll.ghost.v}</span> : null}
              </div>
              <div className={'mlap-hg-diff ' + (diff === 0 ? 'same' : diff < 0 ? 'less' : 'more') + (moved ? ' bump' : '')} role="status">
                <span>{trFl(diff === 0 ? 'hg.same' : 'hg.diff')}</span>
                <b key={counted}>{diff === 0 ? '✓' : (diff > 0 ? '+' : '−') + trFl('hg.nGalon', { n: Math.abs(diff) })}</b>
              </div>
            </div>

            {reasonOn ? (
              <div className={'mlap-hg-reason' + (diff === 0 ? ' closing' : '')} aria-hidden={diff === 0 ? 'true' : undefined} inert={diff === 0 ? '' : undefined}>
                <div className="mlap-label">{trFl('hg.why')} <span className="mlap-hg-req" aria-hidden="true">*</span></div>
                <div className="mlap-chips" role="group" aria-label={trFl('hg.why')}>
                  {DISTGL.COUNT_REASONS.map((r) => <button key={r.key} type="button" className={'mlap-chip-b' + (reason === r.key ? ' on' : '')} aria-pressed={reason === r.key} onClick={() => setReason(r.key)}>{trFl(HG_REASON_LABEL[r.key])}</button>)}
                </div>
                <input className="mlap-text" value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} placeholder={trFl('hg.notePh')} aria-label={trFl('hg.notePh')} />
              </div>
            ) : null}

            {plan.short > 0 ? (
              <>
                <div className="mlap-label">{trFl('hg.shortT', { n: plan.short })}</div>
                <div className="mlap-hg-opts" role="radiogroup" aria-label={trFl('hg.shortT', { n: plan.short })}>
                  <button type="button" role="radio" aria-checked={shortfall === 'tagih'} className={opt(shortfall === 'tagih')} disabled={!plan.allowed.tagih} onClick={() => setShortfall('tagih')}>
                    <span className="mlap-hg-radio" aria-hidden="true" />
                    <span className="mlap-grow"><b>{trFl('hg.optTagih')}</b>
                      {plan.allowed.tagih
                        ? <span className="sb">{trFl('hg.optTagihSub')} {trFl('hg.times', { n: plan.short, v: FIELDLOGIC.fmtRp(price) })} = <span className="mlap-hg-price">{FIELDLOGIC.fmtRp(plan.short * price)}</span></span>
                        : <span className="mlap-hg-tag">{trFl(canTagih ? 'hg.noPrice' : 'hg.askOffice')}</span>}
                    </span>
                  </button>
                  {reasonRow && reasonRow.outsideOk ? (
                    <button type="button" role="radio" aria-checked={shortfall === 'di_luar'} className={opt(shortfall === 'di_luar')} onClick={() => setShortfall('di_luar')}>
                      <span className="mlap-hg-radio" aria-hidden="true" />
                      <span className="mlap-grow"><b>{trFl('hg.optOutside')}</b><span className="sb">{trFl('hg.optOutsideSub')}</span></span>
                    </button>
                  ) : null}
                  <button type="button" role="radio" aria-checked={shortfall === 'tidak_ditagih'} className={opt(shortfall === 'tidak_ditagih')} onClick={() => setShortfall('tidak_ditagih')}>
                    <span className="mlap-hg-radio" aria-hidden="true" />
                    <span className="mlap-grow"><b>{trFl('hg.optWaive')}</b><span className="sb">{trFl('hg.optWaiveSub')}</span><span className="mlap-hg-tag">{trFl('hg.needAcc')}</span></span>
                  </button>
                </div>
                {shortfall === 'tagih' ? (
                  <div className="mlap-card mlap-hg-paybox">
                    <div className="mlap-hg-priceline"><span>{trFl('hg.grT')}</span><b>{FIELDLOGIC.fmtRp(plan.amount)}</b></div>
                    <div className="mlap-hg-paywrap">
                      <div className="mlap-label">{trFl('hg.paidBy')}</div>
                      <div className="mlap-hg-pay" role="group" aria-label={trFl('hg.paidBy')}>
                        {['tunai', 'bon', 'transfer'].map((m) => <button key={m} type="button" className={'mlap-hg-pay-b' + (pay === m ? ' on' : '')} aria-pressed={pay === m} onClick={() => setPay(m)}>{trFl(HG_PAY_LABEL[m])}</button>)}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}

            <div className="mlap-label mlap-hg-photolbl">{trFl('hg.photoT')} <span className="mlap-hg-req" aria-hidden="true">*</span></div>
            <div className={'mlap-hg-photo' + (photo ? ' has' : '')} onChangeCapture={shutter}>
              <FldPhoto api={api} value={photo} onChange={takePhoto} title={trFl('hg.photoT')} req={trFl('hg.photoReq')} hintKey="hg.photoHint" camLabel={trFl('fld.camera')}
                aside={photo ? <span className="mlap-hg-pmeta"><b>{trFl('hg.photoSaved')}</b><span>{hgWhen(photo.takenAt)}</span><span>{trFl(photo.lat != null ? 'hg.photoGps' : 'hg.photoNoGps')}</span></span> : null} />
            </div>

            <div className="mlap-notice info mlap-hg-who" role="note">
              <span className="mlap-hg-ava" aria-hidden="true">{hgInitials(me && me.name)}</span>
              <span>{trFl('hg.whoPre')} <b>{who}</b>. {trFl('hg.whoNum')} <b>{trFl('hg.whoLive')}</b> {trFl('hg.whoPost')}</span>
            </div>
          </>
        )}
        {err && <div className="mlap-err" role="alert">{err}</div>}
      </div>
      <FldCtaBar hint={hint}>
        <button type="button" className="mlap-btn primary mlap-hg-cta" disabled={busy || !!saved || !!pendingSale || !photo || !!plan.blocked || needPay} onClick={send}>
          {sheen ? <span key={sheen} className="mlap-hg-sheen" aria-hidden="true" /> : null}
          {plan.effect === 'ganti_rugi' ? trFl('hg.saveTagih', { v: FIELDLOGIC.fmtRp(plan.amount) }) : trFl('hg.save')}
        </button>
      </FldCtaBar>
      {flash ? <div key={flash} className="mlap-hg-flash" aria-hidden="true" /> : null}
      {saved ? <HgSaved result={saved} before={rec} name={c.name} me={me} onClose={() => onDone(saved)} onHistory={onHistory ? () => onHistory(saved) : null} /> : null}
    </div>
  );
}

// HITUNGAN TERSIMPAN — the glass sheet after a count (mockup 1): a drawn check, before → after counting up,
// the details coming in one by one; pulled down (or Selesai) to close. `before` is the fallback when the
// result carries no stamped balance; `name` / `me` fill the sub line and the counter when the result has
// none (practice copy). `onHistory` (optional) adds "Lihat riwayat galon".
function HgSaved({ result, before, name, me, onClose, onHistory }) {
  const drag = useFldSheetDrag(onClose);
  const a = (result && result.adjustment) || {};
  const from = a.before != null ? a.before : before;
  const to = result && result.gallonsHeld != null ? result.gallonsHeld : (a.after != null ? a.after : from);
  const [shown, setShown] = uShg(from);
  uEhg(() => {
    if (from === to || hgReduced()) { setShown(to); return undefined; }
    let raf = 0; let t0 = 0;
    const step = (t) => { if (!t0) t0 = t; const p = Math.min(1, (t - t0) / 700); const e = 1 - Math.pow(1 - p, 3); setShown(Math.round(from + (to - from) * e)); if (p < 1) raf = requestAnimationFrame(step); };
    const wait = setTimeout(() => { raf = requestAnimationFrame(step); }, 300);
    return () => { clearTimeout(wait); cancelAnimationFrame(raf); };
  }, [from, to]);
  const who = a.createdByName ? hgWho(a.createdByName, hgRole(a.createdByRole)) : hgWho(me && me.name, me && me.role);
  const why = a.countReason ? [trFl(HG_REASON_LABEL[a.countReason] || 'hg.r_lainnya'), a.note].filter(Boolean).join(' · ') : trFl('hg.matched');
  const outside = a.effect === 'outside';
  const waive = !outside && !!((result && result.waiveRequest) || a.waiveRequestId || a.shortfall === 'tidak_ditagih');
  const waived = Math.max(0, from - (a.after != null ? a.after : to));
  // a ganti rugi shows the money the server actually charged (never the phone's preview)
  const t = result && result.transaction;
  const charged = t && t.kind === 'ganti_rugi' ? t : null;
  const chargedPay = charged ? HG_PAY_LABEL[DISTGL.grPayKey(charged)] : '';   // the Kartu's words (a transfer awaiting proof is not "Masuk bon")
  return (
    <>
      <button type="button" className="mlap-scrim" aria-label={trFl('hg.done')} onClick={onClose} />
      <div className="mlap-sheet mlap-hg-saved" role="dialog" aria-modal="true" aria-label={trFl('hg.savedT')} ref={drag.ref} style={drag.style}>
        <FldGrab handle={drag.handle} />
        <div className="mlap-hg-ok" aria-hidden="true">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <h3>{trFl('hg.savedT')}</h3>
        <div className="mlap-hg-ssub">{trFl('hg.savedSub', { name: name || '' })}</div>
        <div className="mlap-hg-ba" role="img" aria-label={from + ' → ' + to}>
          <span className="old">{from}</span><FldSvg n="arrowRight" s={22} sw={2.2} /><span className="new">{shown}</span>
        </div>
        {waive ? <FldNotice tone="warn" title={trFl('hg.waitAcc')} sub={trFl('hg.waitAccSub', { n: waived })} /> : null}
        {outside ? <FldNotice tone="info" title={trFl('hg.outside', { n: a.outsideQty || 0 })} /> : null}
        <div className="mlap-card mlap-hg-stagger">
          <div className="mlap-kv"><span>{trFl('hg.countedBy')}</span><b>{who || '—'}</b></div>
          <div className="mlap-kv"><span>{trFl('hg.when')}</span><b>{hgWhen(a.countedAt || a.createdAt || Date.now())}</b></div>
          <div className="mlap-kv"><span>{trFl('hg.noteT')}</span><b>{why}</b></div>
          {charged ? <div className="mlap-kv"><span>{trFl('hg.grT')}</span><b className="bon">{FIELDLOGIC.fmtRp(Number(charged.amount)) + (chargedPay ? ' · ' + trFl(chargedPay) : '')}</b></div> : null}
          <div className="mlap-kv"><span>{trFl('hg.photoK')}</span><b className="ok">{trFl('hg.photoOk')}</b></div>
        </div>
        <button type="button" className="mlap-btn primary" onClick={onClose}>{trFl('hg.done')}</button>
        {onHistory ? <button type="button" className="mlap-btn" onClick={onHistory}>{trFl('hg.history')}</button> : null}
      </div>
    </>
  );
}

// ══ RIWAYAT GALON (mockup 1 "PHONE 2") + KARTU GALON (mockup 2 "PHONE B") ═════════════════════════════
// Riwayat reads api.gallonHistory (the staff view: newest first, reasons, who reversed what). The Kartu reads
// api.gallonCard — the very card object the customer's link renders (doc-render renderGallonCard): public
// effect words, first names, no roles, reasons or notes.
// The mockups' own glyphs (the field icon set has no count/arrow/share/WhatsApp glyph of this shape).
const HG_PATH = {
  count: 'M4 7h16M4 12h10M4 17h6', up: 'M12 19V5M6 11l6-6 6 6', down: 'M12 5v14M6 13l6 6 6-6',
  trip: 'M3 7h11v9H3zM14 10h4l3 3v3h-7', loss: 'M12 3v4M8 21h8M9 7h6l1 14H8z', share: 'M12 15V3M7 8l5-5 5 5M5 13v7h14v-7',
  wa: 'M12 2a10 10 0 00-8.6 15.1L2 22l5-1.3A10 10 0 1012 2z',
};
function HgIco({ k, s }) {
  if (k === 'wa') return <svg width={s || 18} height={s || 18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={HG_PATH.wa} /></svg>;
  return (
    <svg width={s || 18} height={s || 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={HG_PATH[k] || HG_PATH.count} />
      {k === 'trip' ? <><circle cx="7" cy="17" r="1.6" /><circle cx="17" cy="17" r="1.6" /></> : null}
    </svg>
  );
}
const hgPad = (n) => String(n).padStart(2, '0');
const hgClock = (ms) => { const d = new Date(ms); return hgPad(d.getHours()) + ':' + hgPad(d.getMinutes()); };
const hgDayShort = (ms) => { try { return new Date(ms).toLocaleDateString(trFl('fld.locale'), { day: 'numeric', month: 'short' }); } catch (e) { return ''; } };
const hgStamp = (ms) => hgDayShort(ms) + ', ' + hgClock(ms);   // "3 Okt, 10:05"
const hgIso = (ms) => { const d = new Date(ms); return d.getFullYear() + '-' + hgPad(d.getMonth() + 1) + '-' + hgPad(d.getDate()); };
const hgMonthOf = (ms) => { const d = new Date(ms); let label; try { label = d.toLocaleDateString(trFl('fld.locale'), { month: 'long', year: 'numeric' }); } catch (e) { label = hgIso(ms).slice(0, 7); } return { key: d.getFullYear() + '-' + d.getMonth(), label }; };
// Group rows by calendar month, in the order given.
const hgByMonth = (rows) => rows.reduce((gs, r) => {
  const m = hgMonthOf(r.at); const g = gs[gs.length - 1];
  if (g && g.month === m.key) g.rows.push(r); else gs.push({ key: gs.length + ':' + m.key, month: m.key, label: m.label, rows: [r] });
  return gs;
}, []);
const hgRowWho = (r) => hgWho(r.actorName, hgRole(r.actorRole));
// Photos come through the adaptor (field: api.photo → a data URL); one fetch per photo while the app is open.
const HG_PHOTOS = new Map();
const hgPhoto = (api, id) => {
  if (!HG_PHOTOS.has(id)) {
    HG_PHOTOS.set(id, Promise.resolve(api.photo(id)).then((d) => { if (!d) HG_PHOTOS.delete(id); return d || null; }).catch(() => { HG_PHOTOS.delete(id); return null; }));
    if (HG_PHOTOS.size > 80) HG_PHOTOS.delete(HG_PHOTOS.keys().next().value);
  }
  return HG_PHOTOS.get(id);
};
// The photo sheet's title: what the row was.
const hgPhotoTitle = (r) => trFl(r.type === 'count' ? 'hg.title' : r.type === 'delivery' ? 'hg.kTrip' : r.type === 'ganti_rugi' ? 'hg.grT' : 'fld.proof');
// A number that counts from → to (ease-out, 700 ms after 300 ms), like the saved sheet and the mockup hero.
function useHgCountUp(from, to) {
  const [shown, setShown] = uShg(to);
  uEhg(() => {
    if (from == null || to == null || from === to || hgReduced()) { setShown(to); return undefined; }
    setShown(from);
    let raf = 0; let t0 = 0;
    const step = (t) => { if (!t0) t0 = t; const p = Math.min(1, (t - t0) / 700); const e = 1 - Math.pow(1 - p, 3); setShown(Math.round(from + (to - from) * e)); if (p < 1) raf = requestAnimationFrame(step); };
    const wait = setTimeout(() => { raf = requestAnimationFrame(step); }, 300);
    return () => { clearTimeout(wait); cancelAnimationFrame(raf); };
  }, [from, to]);
  return shown;
}

// A row's small photo (mockup .mini): the gradient until the photo is here.
function HgThumb({ api, id }) {
  const [src, setSrc] = uShg(null);
  uEhg(() => { let live = true; hgPhoto(api, id).then((d) => { if (live) setSrc(d); }); return () => { live = false; }; }, [api, id]);
  return <span className="mlap-hg-mini" aria-hidden="true">{src ? <img src={src} alt="" /> : null}</span>;
}

// One Riwayat row (mockup 1 .hrow): icon, what happened (before → after for a count), the change or the
// balance on the right, when · who, the reason, the photo. A reversed count is struck through and tagged.
function HgHistRow({ api, row, fresh, onPhoto }) {
  const isCount = row.type === 'count';
  const diff = isCount ? (Number(row.counted) || 0) - (Number(row.before) || 0) : 0;
  const out = Number(row.out) || 0; const back = Number(row.in) || 0;
  let ico = 'count'; let tone = 'count'; let title; let right = row.balance; const quotes = [];
  if (isCount) {
    title = trFl('hg.hCount', { a: row.before, b: row.counted });
    right = diff === 0 ? '✓' : (diff > 0 ? '+' : '−') + Math.abs(diff);
    const why = row.countReason ? [trFl(HG_REASON_LABEL[row.countReason] || 'hg.r_lainnya'), row.note].filter(Boolean).join(' · ') : '';
    if (why) quotes.push('"' + why + '"');
    if (row.effect === 'outside' && row.outsideQty) quotes.push(trFl('hg.kElse', { n: row.outsideQty }));
    if (row.effect === 'ganti_rugi' && row.amount != null && !row.void) quotes.push(trFl('hg.kGrSub', { v: FIELDLOGIC.fmtRp(row.amount) + (HG_PAY_LABEL[row.payMethod] ? ' · ' + trFl(HG_PAY_LABEL[row.payMethod]) : '') }));
  } else if (row.type === 'delivery') {
    ico = out > 0 ? 'up' : 'down'; tone = out > 0 ? 'out' : 'in';
    title = row.change === 'void' ? trFl('hg.kTripVoid')
      : (row.change === 'correction' ? trFl('hg.kTripFix') + ': ' : '') + (out && back ? trFl('hg.hOutIn', { o: out, i: back }) : out ? trFl('hg.hOut', { o: out }) : trFl('hg.hIn', { i: back }));
  } else if (row.type === 'ganti_rugi') {
    ico = 'loss'; tone = 'loss';
    title = row.change === 'void' ? trFl('hg.kGrVoid') : row.change === 'correction' ? trFl('hg.kGrFix') : trFl('hg.kGrN', { n: row.qty != null ? Number(row.qty) || 0 : Math.max(0, -(Number(row.delta) || 0)) });
  } else {
    title = trFl(row.type === 'opening' ? 'hg.kOpening' : row.type === 'penyesuaian' ? 'hg.kAdj' : 'hg.kOther');
  }
  if (row.reversed) quotes.push(trFl('hg.reversedBy', { who: row.reversed.byName || '—', why: row.reversed.reason || '—' }));
  if (row.note && !isCount && row.change) quotes.push('"' + row.note + '"');
  const tag = row.reversed ? ['rev', 'hg.reversed'] : row.void && row.type === 'ganti_rugi' && !row.change ? ['rev', 'hg.reversed'] : row.waive && row.waive.status === 'pending' ? ['wait', 'hg.waitAcc'] : null;
  const who = hgRowWho(row);
  const cls = 'mlap-hg-hrow' + (row.reversed ? ' revd' : '') + (fresh ? ' mlap-hg-fresh' : '');
  const inner = (
    <>
      <span className={'mlap-hg-hico ' + tone}><HgIco k={ico} /></span>
      <span className="mlap-hg-hmain">
        <span className="t"><span>{title}</span>{tag ? <span className={'mlap-hg-' + tag[0]}>{trFl(tag[1])}</span> : <b>{right}</b>}</span>
        {row.type === 'opening' ? null : <span className="s">{[hgStamp(row.at), who].filter(Boolean).join(' · ')}</span>}
        {quotes.map((q, i) => <span key={i} className="q">{q}</span>)}
      </span>
      {row.photoId ? <HgThumb api={api} id={row.photoId} /> : null}
    </>
  );
  return row.photoId
    ? <button type="button" className={cls} onClick={() => onPhoto(row)}>{inner}</button>
    : <div className={cls}>{inner}</div>;
}

// RIWAYAT GALON — the gallons at this customer now, when they were last counted and by whom, then every
// count and delivery, newest first, by month (Semua / Hitung / Antar & ambil). `freshId` = the count just
// saved: its row arrives highlighted and the big number counts up to it. `onCount(cust)` (null without the
// cap) opens Hitung galon with the record just read; `onCard()` opens the Kartu Galon.
function HgHistory({ api, cust: c, freshId, onCount, onCard, onBack }) {
  const [h, setH] = uShg(null);
  const [err, setErr] = uShg(null);
  const [reload, setReload] = uShg(0);
  const [seg, setSeg] = uShg('all');
  const [photo, setPhoto] = uShg(null);
  uEhg(() => {
    let live = true; setErr(null);
    api.gallonHistory(c.id).then((x) => { if (live) setH(x || { rows: [] }); }).catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [api, c.id, reload]);
  const held = h ? h.gallonsHeld : c.gallonsHeld;
  const fresh = h && freshId ? (h.rows || []).find((r) => r.id === freshId) : null;
  const shown = useHgCountUp(fresh && fresh.before != null ? fresh.before : held, held);
  // the spring selection under the segment (the dock's liquid blob, at the segment's size)
  const segRef = uRhg(null);
  const [blob, setBlob] = uShg(null);
  uEhg(() => {
    const measure = () => {
      const w = segRef.current; if (!w) return;
      const b = w.querySelector('.mlap-seg-b.on');
      setBlob(b ? { left: b.offsetLeft, top: b.offsetTop, width: b.offsetWidth, height: b.offsetHeight } : null);
    };
    measure(); window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [seg, !!h]);
  const lc = h && h.lastCount;
  const lastWho = lc ? hgWho(lc.byName, hgRole(lc.byRole)) : '';
  const lastWhen = lc ? (fresh && Date.now() - lc.at < 5 * 60000 ? trFl('hg.justNow') : hgDayShort(lc.at)) : '';
  // a view-windowed account sees no count from before its window: that is not "never counted"
  const heroSub = lc ? (lastWho ? trFl('hg.lastBy', { when: lastWhen, who: lastWho }) : trFl('hg.lastAt', { when: lastWhen }))
    : !h ? '' : h.windowed && h.from ? trFl('hg.windowed', { date: hgDayShort(new Date(h.from + 'T00:00').getTime()) }) : trFl('hg.never');
  const rows = h ? (h.rows || []).filter((r) => !r.reversalOf && (seg === 'all' || (seg === 'count' ? r.type === 'count' : r.type === 'delivery'))).slice().reverse() : [];
  const groups = hgByMonth(rows);
  return (
    <div className="mlap-screen mlap-hg-history">
      <FldTop title={trFl('hg.historyRow')} sub={c.name} onBack={onBack} backLabel={trFl('hg.back')} />
      <div className="mlap-body">
        <div className="mlap-card mlap-hg-hero">
          <span className="mlap-hg-heron">{shown == null ? '—' : shown}</span>
          <span className="mlap-grow">
            <span className="mlap-hg-herot">{trFl('hg.heroT')}</span>
            <span className="mlap-hg-heros">{heroSub}</span>
          </span>
        </div>
        <div className={'mlap-hg-seg' + (blob ? ' has-blob' : '')} ref={segRef}>
          {blob ? <span className="mlap-hg-segblob" aria-hidden="true" style={blob} /> : null}
          <FldSeg size="sm" label={trFl('hg.historyRow')} value={seg} onChange={setSeg} options={[['all', trFl('hg.segAll')], ['count', trFl('hg.segCount')], ['trip', trFl('hg.segTrip')]]} />
        </div>
        {err ? <FldNotice tone="warn" alert title={trFl('fld.loadErr')} sub={fldErrMsg(err)} action={trFl('fld.retry')} onAction={() => setReload((n) => n + 1)} />
          : !h ? <div className="mlap-empty">{trFl('fld.loading')}</div>
            : !groups.length ? <div className="mlap-empty">{trFl('hg.empty')}</div>
              : groups.map((g) => (
                <React.Fragment key={g.key}>
                  <div className="mlap-hg-month">{g.label}</div>
                  <div className="mlap-card">
                    {g.rows.map((r) => <HgHistRow key={r.id} api={api} row={r} fresh={!!freshId && r.id === freshId} onPhoto={setPhoto} />)}
                  </div>
                </React.Fragment>
              ))}
        <div className="mlap-ctaspace" />
      </div>
      <FldCtaBar>
        <div className="mlap-hg-ctarow">
          <button type="button" className={'mlap-btn ' + (onCount ? 'mlap-hg-glassbtn' : 'primary')} disabled={!h} onClick={() => onCard()}>{trFl('hg.cardT')}</button>
          {onCount ? <button type="button" className="mlap-btn primary" disabled={!h} onClick={() => onCount(Object.assign({}, c, { gallonsHeld: h.gallonsHeld }))}>{trFl('hg.count')}</button> : null}
        </div>
      </FldCtaBar>
      {photo ? <HgPhotoSheet api={api} photoId={photo.photoId} title={hgPhotoTitle(photo)} stamp={[hgWhen(photo.at), hgRowWho(photo)].filter(Boolean).join(' · ')} onClose={() => setPhoto(null)} /> : null}
    </div>
  );
}

// One Kartu row in the customer page's words (doc-render gcRow), from a card row (public effect words; a
// voided ganti rugi already carries no money): title, the money line, the chips, the icon.
function hgCardRow(r, firstId) {
  const fmtN = (n) => DOCRENDER.fmtNum(n);
  // the ganti rugi money line and gallons in the customer page's own words (doc-render gcMoney / gcGrQty)
  const money = () => DOCRENDER.gcMoney(r);
  const qty = DOCRENDER.gcGrQty(r);
  const eff = r.effect || null;
  const chips = []; let title; let icon = 'trip'; let hl = false; let sub = ''; let diffTxt = ''; let gr = ''; let grQty = 0;
  if (r.type === 'opening') { title = trFl('hg.kOpening'); icon = 'count'; }
  else if (r.type === 'count') {
    icon = 'count';
    if (r.reversalOf) title = trFl('hg.kRevd');
    else {
      const diff = (Number(r.counted) || 0) - (Number(r.before) || 0);
      title = trFl(r.id === firstId ? 'hg.kFirst' : 'hg.kCount', { n: fmtN(r.counted) }) + (eff === 'sama' || diff === 0 ? '' : ' ' + trFl(diff < 0 ? 'hg.kLess' : 'hg.kMore', { n: Math.abs(diff) }));
      // a short count shows its difference where the balance stands (mockup "−2 selisih"); a surplus keeps the chip
      if (diff < 0 && eff !== 'di_luar') { hl = true; diffTxt = '−' + Math.abs(diff); }
      else if (diff > 0 && eff !== 'di_luar') chips.push(['sel', trFl('hg.kSel', { v: '+' + diff })]);
      if (eff === 'di_luar' && r.outsideQty) chips.push(['oth', trFl('hg.kElse', { n: fmtN(r.outsideQty) })]);
      // the money it charged stands on a row of its own under it (mockup: "Ganti rugi 2 galon" … "10 saldo");
      // a voided ganti rugi carries no money on the card, so no row
      if (eff === 'ganti_rugi' && money(qty)) { gr = money(qty); grQty = qty; }
    }
  } else if (r.type === 'ganti_rugi') {
    icon = 'loss';
    title = r.change === 'void' ? trFl('hg.kGrVoid') : r.change === 'correction' ? trFl('hg.kGrFix') : trFl('hg.kGrN', { n: qty });
    sub = money(qty);
  } else if (r.type === 'delivery') {
    title = trFl(r.change === 'void' ? 'hg.kTripVoid' : r.change === 'correction' ? 'hg.kTripFix' : 'hg.kTrip');
    if (r.out) chips.push(['out', trFl('hg.kOut', { n: fmtN(r.out) })]);
    if (r.in) chips.push(['in', trFl('hg.kIn', { n: fmtN(r.in) })]);
  } else { title = trFl(r.type === 'penyesuaian' ? 'hg.kAdj' : 'hg.kOther'); icon = 'count'; }
  return { title, sub, chips, icon, hl, diffTxt, gr, grQty };
}

// The big photo of a row (mockup 2 photo sheet): what it was, when · who, the photo with that stamp on it;
// pulled down (or Tutup) to close.
function HgPhotoSheet({ api, photoId, title, stamp, onClose }) {
  const drag = useFldSheetDrag(onClose);
  const [src, setSrc] = uShg(null);   // null = loading · '' = could not load · data URL
  uEhg(() => {
    let live = true;
    Promise.resolve(HG_PHOTOS.get(photoId) || api.photo(photoId)).then((d) => { if (live) setSrc(d || ''); }).catch(() => { if (live) setSrc(''); });
    return () => { live = false; };
  }, [api, photoId]);
  return (
    <>
      <button type="button" className="mlap-scrim" aria-label={trFl('fld.close')} onClick={onClose} />
      <div className="mlap-sheet mlap-hg-photosheet" role="dialog" aria-modal="true" aria-label={title} ref={drag.ref} style={drag.style}>
        <FldGrab handle={drag.handle} />
        <h3>{title}</h3>
        <div className="mlap-hg-psub">{stamp}</div>
        <div className="mlap-hg-bigphoto">
          {src ? <img src={src} alt={title} /> : <span className="mlap-hg-bigmsg">{trFl(src === '' ? 'fld.h_photoErr' : 'fld.loading')}</span>}
          {stamp ? <span className="mlap-hg-bigstamp">{stamp}</span> : null}
        </div>
        <button type="button" className="mlap-btn primary" onClick={onClose}>{trFl('fld.close')}</button>
      </div>
    </>
  );
}

// KARTU GALON — the customer's proof card, as the customer receives it: the business, the customer, three
// figures (Hitung awal / Ganti rugi / Sekarang), the rows oldest first by month with the balance on the
// right, the footer. A row with a photo opens it big. Kirim ke WhatsApp makes (or reuses) the card's link,
// fills the owner's "Kartu Galon" template and opens wa.me; Cetak opens that link (its page prints).
// The card is api.gallonCard: the same object the link renders, over the same window a link made by this
// account carries. Practice mode makes no link: the adaptor rejects with its own message, shown above the
// buttons.
function HgCard({ api, cust: c, onBack }) {
  const [card, setCard] = uShg(null);
  const [err, setErr] = uShg(null);
  const [reload, setReload] = uShg(0);
  uEhg(() => {
    let live = true; setErr(null);
    api.gallonCard(c.id).then((x) => { if (live) setCard(x || { rows: [], figures: {} }); }).catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [api, c.id, reload]);
  const [biz, setBiz] = uShg({ name: '', company: '' });
  const [tplBody, setTplBody] = uShg(() => (WATPL.DEFAULTS.find((t) => t.id === 'kartu_galon') || {}).body || '');
  uEhg(() => {
    let live = true;
    Promise.resolve(typeof loadDocSettings === 'function' ? loadDocSettings() : null)
      .then((st) => { const id = (st && st.identity) || {}; if (live) setBiz({ name: id.bizName || '', company: id.company || '' }); }).catch(() => {});
    // the owner may have edited the template in Pengaturan
    Promise.resolve(typeof loadWaTemplates === 'function' ? loadWaTemplates() : WATPL.DEFAULTS)
      .then((list) => { const t = (list || []).find((x) => x.id === 'kartu_galon'); if (live && t && t.body) setTplBody(t.body); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const [link, setLink] = uShg(null);
  const [busy, setBusy] = uShg('');
  const [sendErr, setSendErr] = uShg('');
  const [photo, setPhoto] = uShg(null);
  const phoneOk = !!waE164(c.phone);
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;
  // the letterhead the customer's page prints (name, company line, logo letter) — one helper, so both match
  const brand = DOCRENDER.gallonCardBrand({ bizName: biz.name, company: biz.company });
  const bizName = brand.name;
  const textFor = (url) => WATPL.render(tplBody, { nama: c.name || '', link: url, nama_usaha: bizName });
  // Make (or reuse) the link, then go. A new tab is opened in the tap itself — a phone blocks one opened
  // after waiting for the server — and sent on once the link is here (closed again if it fails).
  const go = (kind) => {
    if (busy || (kind === 'wa' && !phoneOk)) return;
    setSendErr('');
    let tab = null;
    if (kind !== 'share' && !link && api.mode !== 'latihan') { try { tab = window.open('', '_blank'); if (tab) tab.opener = null; } catch (e) { tab = null; } }
    // a server that never answers must not leave an empty tab behind
    const stale = tab ? setTimeout(() => { try { tab.close(); } catch (e) { /* already gone */ } tab = null; }, 15000) : null;
    setBusy(kind);
    (link ? Promise.resolve(link) : api.gallonCardLink(c.id).then((l) => { setLink(l); return l; }))
      .then((l) => {
        const url = l && l.url; if (!url) throw new Error(trFl('hg.linkErr'));
        if (kind === 'share') return navigator.share({ title: trFl('hg.cardT') + ' ' + (c.name || ''), text: textFor(url) }).catch(() => {});
        const href = kind === 'wa' ? waHref(c.phone, textFor(url)) : url;
        if (tab) tab.location.href = href; else window.open(href, '_blank', 'noopener');
        return null;
      })
      .catch((e) => { if (tab) tab.close(); setSendErr(fldErrMsg(e) || trFl('hg.linkErr')); })
      .finally(() => { clearTimeout(stale); setBusy(''); });
  };
  const counted = !!card && card.firstCountAt != null;
  const firstRow = counted ? (card.rows || []).find((r) => r.type === 'count' && !r.reversalOf && r.at === card.firstCountAt) : null;
  const firstId = firstRow ? firstRow.id : null;
  const f = (card && card.figures) || {};
  const groups = card ? hgByMonth(card.rows || []) : [];
  const hint = sendErr ? sendErr : busy ? <span className="muted">{trFl('hg.linking')}</span> : !phoneOk ? trFl('wa.noPhone') : null;
  const cu = (card && card.customer) || { name: c.name, code: c.code, address: c.address };
  // as on the customer's page: the first name only, never a role (the card's rows already carry just that)
  const who = (r) => r.actorName || '';
  return (
    <div className="mlap-screen mlap-hg-card">
      <div className="mlap-top">
        <button type="button" className="mlap-round" aria-label={trFl('fld.back')} onClick={onBack}><FldSvg n="back" s={18} sw={2.4} /></button>
        <div className="mlap-top-t"><h1>{trFl('hg.cardT')}</h1>{card ? <div className="mlap-top-sub">{DOCRENDER.fmtPeriod(card.from, card.to)}</div> : null}</div>
        {canShare ? <button type="button" className="mlap-round mlap-hg-share" aria-label={trFl('hg.share')} disabled={!!busy} onClick={() => go('share')}><HgIco k="share" /></button> : <span aria-hidden="true" />}
      </div>
      <div className="mlap-body">
        <div className="mlap-card">
          <div className="mlap-hg-dochd">
            <div className="mlap-hg-brand"><span className="mlap-hg-logo" aria-hidden="true">{brand.letter}</span><span><b>{brand.name}</b><span>{brand.company}</span></span></div>
            <div><div className="mlap-hg-docnm">{cu.name}</div><div className="mlap-hg-docsb">{[cu.code, cu.address].filter(Boolean).join(' · ')}</div></div>
          </div>
          {card && !counted ? <div className="mlap-hg-nocount">{trFl('hg.kNoCount')}</div> : null}
          <div className="mlap-hg-sum">
            <div><span>{trFl('hg.kAwal')}</span><b>{counted && f.awal != null ? DOCRENDER.fmtNum(f.awal) : '–'}</b></div>
            <div><span>{trFl('hg.grT')}</span><b className="neg">{DOCRENDER.fmtNum(f.gantiRugi || 0)}</b></div>
            <div><span>{trFl('hg.kNow')}</span><b>{f.sekarang == null ? '–' : DOCRENDER.fmtNum(f.sekarang)}</b></div>
          </div>
        </div>
        {err ? <FldNotice tone="warn" alert title={trFl('fld.loadErr')} sub={fldErrMsg(err)} action={trFl('fld.retry')} onAction={() => setReload((n) => n + 1)} />
          : !card ? <div className="mlap-empty">{trFl('fld.loading')}</div>
            : groups.map((g) => (
              <React.Fragment key={g.key}>
                <div className="mlap-hg-month">{g.label}</div>
                <div className="mlap-card">
                  {g.rows.map((r) => {
                    const v = hgCardRow(r, firstId);
                    const inner = (
                      <>
                        <span className={'mlap-hg-lico ' + v.icon}><HgIco k={v.icon} /></span>
                        <span className="mlap-hg-lmain">
                          <span className="t">{v.title}</span>
                          {r.type === 'opening' ? null : <span className="s">{[hgStamp(r.at), who(r)].filter(Boolean).join(' · ')}</span>}
                          {v.sub ? <span className="s">{v.sub}</span> : null}
                          {v.chips.length || r.photoId ? (
                            <span className="mlap-hg-io">
                              {v.chips.map(([k, t]) => <span key={k} className={k}>{t}</span>)}
                              {r.photoId ? <span className="ph"><FldSvg n="camera" s={12} sw={2.2} />{r.type === 'count' ? trFl('hg.kPhoto') : null}</span> : null}
                            </span>
                          ) : null}
                        </span>
                        {v.hl
                          ? <span className="mlap-hg-bal"><b>{v.diffTxt}</b><span>{trFl('hg.kSelisih')}</span></span>
                          : <span className="mlap-hg-bal"><b>{DOCRENDER.fmtNum(r.balance)}</b><span>{trFl('hg.kBal')}</span></span>}
                      </>
                    );
                    const cls = 'mlap-hg-lrow' + (v.hl ? ' hl' : '');
                    const main = r.photoId
                      ? <button type="button" className={cls} onClick={() => setPhoto(r)}>{inner}</button>
                      : <div className={cls}>{inner}</div>;
                    // a count that charged ganti rugi: the money on its own row, as the public page prints it
                    // (display only — the balance after the count is the count's own balance)
                    return (
                      <React.Fragment key={r.id}>
                        {main}
                        {v.gr ? (
                          <div className="mlap-hg-lrow">
                            <span className="mlap-hg-lico loss"><HgIco k="loss" /></span>
                            <span className="mlap-hg-lmain"><span className="t">{trFl('hg.kGrN', { n: v.grQty })}</span><span className="s">{hgDayShort(r.at) + ' · ' + v.gr}</span></span>
                            <span className="mlap-hg-bal"><b>{DOCRENDER.fmtNum(r.balance)}</b><span>{trFl('hg.kBal')}</span></span>
                          </div>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
              </React.Fragment>
            ))}
        <div className="mlap-hg-foot">{trFl('hg.kFoot1')}<br />{trFl('hg.kFoot2')}</div>
        <div className="mlap-ctaspace" />
      </div>
      <FldCtaBar hint={hint}>
        <div className="mlap-hg-ctarow">
          <button type="button" className="mlap-btn mlap-hg-glassbtn" disabled={!!busy} onClick={() => go('print')}>{trFl('hg.print')}</button>
          <button type="button" className="mlap-btn primary" disabled={!!busy || !phoneOk} onClick={() => go('wa')}><HgIco k="wa" />{trFl('hg.sendWa')}</button>
        </div>
      </FldCtaBar>
      {photo ? <HgPhotoSheet api={api} photoId={photo.photoId} title={hgPhotoTitle(photo)} stamp={[hgWhen(photo.at), who(photo)].filter(Boolean).join(' · ')} onClose={() => setPhoto(null)} /> : null}
    </div>
  );
}

// ── GALON KITA (mockup 3 "PHONE 1") — an OFFICE screen (owner, GM, warehouse): where every gallon is, how much of
// "di pelanggan" is proven by a recent counted photo, who is out of date (Minta hitung), this month's shortages, and
// the legacy gallon adjustments still pending. Unlike the field screens above it reads the office API client itself.
// DISTGL.staleBucket's out-of-date buckets in the segment's order ('fresh' customers are never listed).
const HG_STALE = ['never', 'gt60', 'mid'];
const HG_STALE_SEG = { never: 'hg.gkSegNever', gt60: 'hg.gkSegGt60', mid: 'hg.gkSegMid' };
const HG_AGE_CLS = { never: 'never', gt60: 'old', mid: 'mid' };
// The four places a gallon can be, in the bar's order: [key, label, stock field]. The colour is --mlap-hg-<key>.
const HG_PLACES = [['depot', 'hg.gkDepot', 'atDepot'], ['armada', 'hg.gkArmada', 'atArmada'], ['cust', 'hg.gkCust', 'atCustomers'], ['loss', 'hg.gkLoss', 'rusakHilang']];
const hgData = (r) => (r && r.data !== undefined ? r.data : r);
const hgNum = (n) => (Number(n) || 0).toLocaleString('id-ID');
// The owner's "belum diperbarui" limit is kept between 7 and 180 days.
const hgStaleDays = (n) => Math.max(7, Math.min(180, Math.round(Number(n) || 30)));
const hgAdjReason = (r) => { const k = 'adj.reason.' + r; const v = trFl(k); return v && v !== k ? v : (r || ''); };
// The overview with one out-of-date row changed in place (a cancelled request shows at once).
const hgPatchRow = (d, id, patch) => (d && d.stale ? Object.assign({}, d, { stale: HG_STALE.reduce((o, b) => { o[b] = (d.stale[b] || []).map((r) => (r.customerId === id ? Object.assign({}, r, patch) : r)); return o; }, {}) }) : d);

// Batalkan a legacy pending adjustment: the reason is required (the office reverse modal, as a glass sheet).
function HgKitaWhy({ adj, note, busy, onNote, onSend, onClose }) {
  const close = () => { if (!busy) onClose(); };
  const drag = useFldSheetDrag(close);
  return (
    <>
      <button type="button" className="mlap-scrim" aria-label={trFl('fld.cancel')} onClick={close} />
      <div className="mlap-sheet mlap-hg-whysheet" role="dialog" aria-modal="true" aria-label={trFl('hg.revT')} ref={drag.ref} style={drag.style}>
        <FldGrab handle={drag.handle} />
        <FldSheetHead title={trFl('hg.revT')} sub={[adj.customerName, adj.before + ' → ' + adj.after].filter(Boolean).join(' · ')} onClose={close} />
        <FldNotice tone="warn" title={trFl('hg.revInfo')} />
        <label className="mlap-label mlap-hg-label" htmlFor="mlap-hg-why">{trFl('hg.revWhy')} <span className="mlap-hg-req">*</span></label>
        <textarea id="mlap-hg-why" className="mlap-text mlap-hg-why" maxLength={300} value={note} placeholder={trFl('hg.revWhyPh')} onChange={(e) => onNote(e.target.value)} />
        <div className="mlap-actions">
          <button type="button" className="mlap-btn" disabled={busy} onClick={close}>{trFl('fld.cancel')}</button>
          <button type="button" className="mlap-btn danger" disabled={!note.trim() || busy} onClick={onSend}>{busy ? '…' : trFl('hg.gkReverse')}</button>
        </div>
      </div>
    </>
  );
}

// GALON KITA. `fleet` = the office's armada filter ('all' = every armada in the account's scope), `isGmOwner` =
// may approve / cancel legacy adjustments, `canSetStale` = holds the settings cap (the "belum diperbarui" limit).
function HgGalonKita({ fleet, isGmOwner, canSetStale }) {
  const galon = API.distribusi.galon; const customers = API.distribusi.customers; const settings = API.settings;
  const fl = fleet && fleet !== 'all' ? fleet : '';
  const [d, setD] = uShg(null);
  const [err, setErr] = uShg(null);
  const [reload, setReload] = uShg(0);
  const [pick, setPick] = uShg(null);              // the bar part whose breakdown is open
  const [seg, setSeg] = uShg('never');
  const [q, setQ] = uShg(null);                    // the name search; null = closed
  const [sel, setSel] = uShg(() => new Set());     // checked customer ids (any segment)
  const [asked, setAsked] = uShg(() => new Map()); // the pop only: customerId → its place in the stagger (never the state)
  const [inFlight, setInFlight] = uShg(() => new Set());   // asked, the server's answer not reloaded yet
  const [bulkBusy, setBulkBusy] = uShg(false);
  const [cancelFor, setCancelFor] = uShg(null);    // the row whose open request is being cancelled
  const [whyOpen, setWhyOpen] = uShg(false);       // "Tidak ditagih" → its reasons
  const [revFor, setRevFor] = uShg(null);
  const [revNote, setRevNote] = uShg('');
  const [busy, setBusy] = uShg(false);
  const [stale, setStale] = uShg(null);            // the limit being edited; null = not editing
  const [toast, setToast] = uShg({ m: '', on: false });
  const [covOn, setCovOn] = uShg(false);
  const toastT = uRhg(0);
  uEhg(() => {
    let live = true;
    galon.overview(fl || undefined).then((r) => { if (live) { setD(hgData(r) || null); setErr(null); } }).catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [fl, reload]);
  uEhg(() => () => clearTimeout(toastT.current), []);
  uEhg(() => { setSel(new Set()); }, [fl]);   // a checked customer of another armada is never sent
  // the coverage track fills 200 ms after the figures arrive (mockup)
  uEhg(() => { if (!d || covOn) return undefined; const t = setTimeout(() => setCovOn(true), 200); return () => clearTimeout(t); }, [!!d]);
  const flash = (m) => { setToast({ m, on: true }); clearTimeout(toastT.current); toastT.current = setTimeout(() => setToast((t) => ({ m: t.m, on: false })), 1800); };
  const refresh = () => setReload((n) => n + 1);
  const failed = (e) => flash(fldErrMsg(e) || trFl('fld.loadErr'));
  // the spring selection under the segment (as in Riwayat galon)
  const segRef = uRhg(null);
  const [blob, setBlob] = uShg(null);
  uEhg(() => {
    const measure = () => {
      const w = segRef.current; if (!w) return;
      const b = w.querySelector('.mlap-seg-b.on');
      setBlob(b ? { left: b.offsetLeft, top: b.offsetTop, width: b.offsetWidth, height: b.offsetHeight } : null);
    };
    measure(); window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [seg, !!d]);

  const st = (d && d.stock) || {}; const cov = (d && d.coverage) || {}; const mo = (d && d.month) || {};
  const days = d ? d.staleDays : 30;
  const shown = useHgCountUp(0, d ? Number(st.totalDimiliki) || 0 : null);
  const parts = HG_PLACES.map(([k, label, f]) => ({ k, label, n: Number(st[f]) || 0 }));
  const rusak = st.rusak || {}; const byFleet = st.armadaByFleet || {};
  const breakdown = {
    depot: [[trFl('hg.gkDepotMain'), st.atDepot]],
    armada: Object.keys(byFleet).sort().map((f) => [f, byFleet[f]]),
    cust: [[trFl('hg.gkProven', { n: days }), cov.heldProven], [trFl('hg.gkStaleRow'), Math.max(0, (Number(st.atCustomers) || 0) - (Number(cov.heldProven) || 0))]],
    loss: [[trFl('hg.gkPaid'), rusak.dibayar], [trFl('hg.gkBroken'), rusak.pecahGudang], [trFl('hg.gkLostFree'), rusak.hilangTidakDitagih], [trFl('hg.gkLostWait'), rusak.menungguAcc]],
  };
  const choose = (k) => setPick((c) => (c === k ? null : k));
  // "dihitung dalam N hari" is a share of CUSTOMERS (counted recently with a photo / active), as the mockup's 231 of 340
  const custPct = cov.activeCustomers ? Math.round(((Number(cov.countedRecent) || 0) / cov.activeCustomers) * 100) : 0;

  const ql = (q || '').trim().toLowerCase();
  const lists = {};
  HG_STALE.forEach((b) => { lists[b] = ((d && d.stale && d.stale[b]) || []).filter((r) => !ql || String(r.name || '').toLowerCase().includes(ql) || String(r.code || '').toLowerCase().includes(ql)); });
  const anyStale = HG_STALE.some((b) => ((d && d.stale && d.stale[b]) || []).length);
  const rows = lists[seg] || [];
  // Diminta is the server's open request on the row; a row only counts as asked on its own while its request is on the way
  const isOpen = (r) => !!r.countRequest || inFlight.has(r.customerId);
  const free = rows.filter((r) => !isOpen(r));
  const allOn = free.length > 0 && free.every((r) => sel.has(r.customerId));
  const chosen = [...sel];
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSel((s) => { const n = new Set(s); free.forEach((r) => { if (allOn) n.delete(r.customerId); else n.add(r.customerId); }); return n; });
  // Minta hitung: the buttons pop at once, 90 ms apart (mockup), then the overview is read again and the rows show what
  // the server holds: a customer it skipped (out of scope, gone) reads Minta hitung again. At most 200 customers per
  // request, so a long selection goes in turns.
  const ask = (ids, bulk) => {
    const todo = ids.filter((id) => !inFlight.has(id)); if (!todo.length) return;
    if (bulk) setBulkBusy(true);
    setInFlight((f) => { const n = new Set(f); todo.forEach((id) => n.add(id)); return n; });
    setAsked((m) => { const n = new Map(m); todo.forEach((id, i) => n.set(id, i)); return n; });
    setSel((s) => { const n = new Set(s); todo.forEach((id) => n.delete(id)); return n; });
    const turns = []; for (let i = 0; i < todo.length; i += 200) turns.push(todo.slice(i, i + 200));
    turns.reduce((p, part) => p.then((acc) => galon.requestCounts({ customerIds: part }).then((r) => { const x = hgData(r) || {}; return { created: acc.created + (Number(x.created) || 0), skipped: acc.skipped + (Number(x.skipped) || 0) }; })), Promise.resolve({ created: 0, skipped: 0 }))
      .then((t) => { flash(!t.created && t.skipped ? trFl('hg.gkToastSkip', { n: t.skipped }) : trFl('hg.gkToast')); })
      .catch(failed)
      .then(() => galon.overview(fl || undefined).then((r) => { const x = hgData(r) || null; if (x) { setD(x); setErr(null); } return x; }).catch(() => null))
      .then((x) => {
        const held = new Set(); if (x && x.stale) HG_STALE.forEach((b) => (x.stale[b] || []).forEach((r) => { if (r.countRequest) held.add(r.customerId); }));
        setAsked((m) => { const n = new Map(m); todo.forEach((id) => { if (!held.has(id)) n.delete(id); }); return n; });
        setInFlight((f) => { const n = new Set(f); todo.forEach((id) => n.delete(id)); return n; });
        if (bulk) setBulkBusy(false);
        if (!x) refresh();   // the read failed: try once more the usual way
      });
  };
  const cancelAsk = () => {
    const r = cancelFor; setCancelFor(null); if (!r || !r.countRequest) return;
    galon.cancelRequest(r.countRequest.id)
      .then(() => { setAsked((m) => { const n = new Map(m); n.delete(r.customerId); return n; }); setD((x) => hgPatchRow(x, r.customerId, { countRequest: null })); flash(trFl('hg.gkCancelled')); refresh(); })
      .catch(failed);
  };
  const approve = (a) => {
    if (busy) return; setBusy(true);
    customers.approveAdjustment(a.id).then(() => { setBusy(false); flash(trFl('hg.gkApproved')); refresh(); }).catch((e) => { setBusy(false); failed(e); });
  };
  const reverse = () => {
    const why = revNote.trim(); if (!revFor || !why || busy) return; setBusy(true);
    customers.reverseAdjustment(revFor.id, { reason: why })
      .then(() => { setBusy(false); setRevFor(null); flash(trFl('hg.gkReversedT')); refresh(); })
      .catch((e) => { setBusy(false); failed(e); });   // the sheet stays open with the reason
  };
  const saveStale = () => {
    if (busy || stale == null) return; const n = hgStaleDays(stale); setBusy(true);
    settings.set('gallonCount', { staleDays: n }).then(() => { setBusy(false); setStale(null); flash(trFl('hg.gkStaleSaved')); refresh(); }).catch((e) => { setBusy(false); failed(e); });
  };
  const wbr = mo.waivedByReason || {};
  const waived = Object.keys(HG_REASON_LABEL).filter((k) => wbr[k]).map((k) => [trFl(HG_REASON_LABEL[k]), wbr[k]]);
  const legacy = (d && d.legacyPending) || [];
  const kv = ([label, n], i) => <div key={i} className="mlap-kv mlap-hg-kv"><span>{label}</span><b>{hgNum(n)}</b></div>;
  const rowSub = (r) => [r.armada, r.lastCountAt ? trFl('hg.gkRowLast', { date: hgDayShort(r.lastCountAt), n: hgNum(r.gallonsHeld) }) : trFl('hg.gkRowNever', { n: hgNum(r.gallonsHeld) })].filter(Boolean).join(' · ');

  return (
    <div className="mlap-screen mlap-hg-kita">
      <div className="mlap-head">
        <div className="mlap-head-t"><span className="mlap-eyebrow">{trFl('hg.gkEyebrow')}</span><h1>{trFl('hg.gkTitle')}</h1></div>
        <div className="mlap-head-act">
          <button type="button" className="mlap-round" aria-label={trFl('hg.gkSearch')} aria-expanded={q != null} onClick={() => setQ((x) => (x == null ? '' : null))}><FldSvg n="search" s={18} sw={2.2} /></button>
        </div>
      </div>
      {err ? <div className="mlap-body"><FldNotice tone="warn" alert title={trFl('fld.loadErr')} sub={fldErrMsg(err)} action={trFl('fld.retry')} onAction={refresh} /></div> : null}
      {!d ? (err ? null : <div className="mlap-empty">{trFl('fld.loading')}</div>) : (
        <div className="mlap-body mlap-hg-grid">
          <div className="mlap-card mlap-hg-total">
            <span className="mlap-eyebrow">{trFl('hg.gkTotal')}</span>
            <div className="mlap-hg-big"><span>{shown == null ? '—' : hgNum(shown)}</span><small>{trFl('hg.gkUnit')}</small></div>
            <div className="mlap-hg-totsb">{fl ? trFl('hg.gkOneFleet', { fleet: fl }) : trFl('hg.gkAllFleets')}</div>
            <div className={'mlap-hg-bar' + (pick ? ' dim' : '')} aria-hidden="true">
              {parts.filter((p) => p.n > 0).map((p) => <i key={p.k} className={pick === p.k ? 'on' : ''} style={{ flex: p.n, background: 'var(--mlap-hg-' + p.k + ')' }} onClick={() => choose(p.k)} />)}
            </div>
            <div className="mlap-hg-legend">
              {parts.map((p) => (
                <button key={p.k} type="button" className={'mlap-hg-lg' + (pick === p.k ? ' on' : '')} aria-expanded={pick === p.k} onClick={() => choose(p.k)}>
                  <span className="mlap-hg-dot" style={{ background: 'var(--mlap-hg-' + p.k + ')' }} />
                  <span><b>{hgNum(p.n)}</b><span>{trFl(p.label)}</span></span>
                </button>
              ))}
            </div>
            {pick ? (
              <div key={pick} className="mlap-hg-reveal">
                {breakdown[pick].length ? breakdown[pick].map(kv) : <div className="mlap-kv mlap-hg-kv"><span>{trFl('hg.gkNoArmada')}</span><b>0</b></div>}
              </div>
            ) : null}
          </div>

          <div className="mlap-hg-blk">
            <div className="mlap-label mlap-hg-label">{trFl('hg.gkCovLabel')}</div>
            <div className="mlap-card mlap-hg-cov">
              <div className="mlap-hg-covl"><b>{custPct}%</b><span>{trFl('hg.gkCovPct', { n: days })}</span></div>
              <div className="mlap-hg-track"><span style={{ width: (covOn ? custPct : 0) + '%' }} /></div>
              <div className="mlap-hg-covs">{trFl('hg.gkCovSub', { a: hgNum(cov.countedRecent), b: hgNum(cov.activeCustomers), x: hgNum(cov.heldProven), y: hgNum(cov.heldTotal) })}</div>
            </div>
          </div>

          <div className="mlap-hg-blk mlap-hg-dueblk">
            {q != null ? <input className="mlap-text mlap-hg-search" type="search" autoFocus value={q} placeholder={trFl('hg.gkSearchPh')} aria-label={trFl('hg.gkSearchPh')} onChange={(e) => setQ(e.target.value)} /> : null}
            <div className={'mlap-hg-seg' + (blob ? ' has-blob' : '')} ref={segRef}>
              {blob ? <span className="mlap-hg-segblob" aria-hidden="true" style={blob} /> : null}
              <FldSeg size="sm" label={trFl('hg.gkStaleRow')} value={seg} onChange={setSeg} options={HG_STALE.map((b) => [b, <>{trFl(HG_STALE_SEG[b], { n: days })}<em>{lists[b].length}</em></>])} />
            </div>
            <div className="mlap-card" key={seg}>
              {free.length ? (
                <label className="mlap-hg-pickall"><input type="checkbox" className="mlap-hg-check" checked={allOn} onChange={toggleAll} /><span>{trFl('hg.gkPickAll')}</span><em>{free.length}</em></label>
              ) : null}
              {rows.length ? rows.map((r, i) => {
                const open = isOpen(r); const fresh = open && asked.has(r.customerId);   // the pop, only on a row that is asked
                return (
                  <div key={r.customerId} className="mlap-hg-crow" style={{ animationDelay: Math.min(i, 12) * 0.05 + 's' }}>
                    <input type="checkbox" className="mlap-hg-check" aria-label={trFl('hg.gkPick', { name: r.name })} checked={!open && sel.has(r.customerId)} disabled={open} onChange={() => toggle(r.customerId)} />
                    <span className="mlap-hg-cmain">
                      <span className="nm">{r.name}</span>
                      <span className="sb">{rowSub(r)}</span>
                      <span className={'mlap-hg-age ' + (HG_AGE_CLS[r.bucket] || HG_AGE_CLS[seg])}>{r.ageDays == null ? trFl('hg.gkSegNever') : trFl('hg.gkAgeDays', { n: r.ageDays })}</span>
                    </span>
                    {open
                      ? <button type="button" className={'mlap-hg-ask done' + (fresh ? ' pop' : '')} style={fresh ? { animationDelay: asked.get(r.customerId) * 0.09 + 's' } : undefined} disabled={!r.countRequest} title={trFl('hg.gkCancelBtn')} onClick={() => setCancelFor(r)}>{trFl(fresh ? 'hg.gkAskedNew' : 'hg.gkAsked')}</button>
                      : <button type="button" className="mlap-hg-ask" onClick={() => ask([r.customerId], false)}>{trFl('hg.gkAsk')}</button>}
                  </div>
                );
              }) : <div className="mlap-empty">{trFl('hg.gkEmptySeg')}</div>}
            </div>
          </div>

          <div className="mlap-hg-blk">
            <div className="mlap-label mlap-hg-label">{trFl('hg.gkSel')}</div>
            <div className="mlap-card">
              <div className="mlap-hg-sel">
                <div><span>{trFl('hg.gkShort')}</span><b className={mo.shortQty ? 'neg' : ''}>{mo.shortQty ? '−' + hgNum(mo.shortQty) : '0'}</b><small>{trFl('hg.gkShortSub', { n: hgNum(mo.shortCustomers) })}</small></div>
                <div><span>{trFl('hg.gkBilled')}</span><b>{hgNum(mo.billedQty)}</b><small>{FIELDLOGIC.fmtRp(mo.billedAmount)}</small></div>
                <button type="button" disabled={!waived.length} aria-expanded={whyOpen && !!waived.length} onClick={() => setWhyOpen((o) => !o)}><span>{trFl('hg.optWaive')}</span><b>{hgNum(mo.waivedQty)}</b><small>{waived.length ? trFl('hg.gkSeeWhy') : ' '}</small></button>
              </div>
              {whyOpen && waived.length ? <div className="mlap-hg-reveal">{waived.map(kv)}</div> : null}
              <div className="mlap-kv mlap-hg-kv"><span>{trFl('hg.gkOutside')}</span><b>{trFl('hg.nGalon', { n: hgNum(mo.outsideQty) })}</b></div>
            </div>
          </div>

          {isGmOwner && legacy.length ? (
            <div className="mlap-hg-blk">
              <div className="mlap-label mlap-hg-label">{trFl('hg.gkLegacy')}</div>
              <div className="mlap-card">
                {legacy.map((a) => (
                  <div key={a.id} className="mlap-hg-legrow">
                    <span className="mlap-hg-cmain">
                      <span className="nm">{a.customerName || '—'}</span>
                      <span className="sb">{trFl('hg.gkLegacySub', { a: a.before, b: a.after, why: hgAdjReason(a.reason) })}</span>
                      <span className="sb">{[a.createdAt ? hgStamp(a.createdAt) : '', a.createdByName].filter(Boolean).join(' · ')}</span>
                      {a.note ? <span className="q">"{a.note}"</span> : null}
                    </span>
                    <span className="mlap-hg-legact">
                      <button type="button" className="mlap-btn" disabled={busy} onClick={() => approve(a)}>{trFl('hg.gkApprove')}</button>
                      <button type="button" className="mlap-btn danger" disabled={busy} onClick={() => { setRevFor(a); setRevNote(''); }}>{trFl('hg.gkReverse')}</button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {canSetStale ? (
            <div className="mlap-card mlap-hg-stale">
              {stale == null ? (
                <div className="mlap-kv mlap-hg-kv"><span>{trFl('hg.gkStaleT', { n: days })}</span><button type="button" className="mlap-btn mlap-hg-small" onClick={() => setStale(days)}>{trFl('hg.gkStaleEdit')}</button></div>
              ) : (
                <>
                  <FldStepper label={trFl('hg.gkStaleT', { n: stale })} hint={trFl('hg.gkStaleHint')} value={stale} onChange={(v) => setStale(hgStaleDays(v))} min={7} max={180} />
                  <div className="mlap-actions mlap-hg-staleact">
                    <button type="button" className="mlap-btn" disabled={busy} onClick={() => setStale(null)}>{trFl('fld.cancel')}</button>
                    <button type="button" className="mlap-btn primary" disabled={busy || stale === days} onClick={saveStale}>{trFl('hg.gkStaleSave')}</button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      )}
      {d && anyStale ? (
        <div className="mlap-hg-bulk">
          <button type="button" className="mlap-btn primary" disabled={!chosen.length || bulkBusy} onClick={() => ask(chosen, true)}>
            {chosen.length && !bulkBusy ? <span className="mlap-sheen" aria-hidden="true" /> : null}{trFl('hg.gkAskAll', { n: chosen.length })}
          </button>
        </div>
      ) : null}
      <div className={'mlap-hg-toast' + (toast.on ? ' on' : '')} role="status" aria-live="polite">{toast.m}</div>
      {cancelFor ? <FldSheet title={trFl('hg.gkCancelT')} body={trFl('hg.gkCancelB', { name: cancelFor.name })} confirmLabel={trFl('hg.gkCancelBtn')} danger onConfirm={cancelAsk} onClose={() => setCancelFor(null)} /> : null}
      {revFor ? <HgKitaWhy adj={revFor} note={revNote} busy={busy} onNote={(v) => setRevNote(v.slice(0, 300))} onSend={reverse} onClose={() => setRevFor(null)} /> : null}
    </div>
  );
}

// MINTA HITUNG — the reminder on the driver's stop card (mockup 3 "PHONE 2"): the office or warehouse asked for
// a count at this customer. `request` = { id, source, note, createdAt, requestedByName }; `onCount` opens the
// count view (absent when the account may not count: the card stays, the button goes). It vanishes by itself: once a count is saved the next board refresh returns countRequest null.
function HgRemind({ request, onCount }) {
  if (!request) return null;
  const when = hgWhen(request.createdAt);
  const sub = request.requestedByName ? trFl('hg.reqBy', { who: request.requestedByName, when }) : trFl('hg.reqAt', { when });
  return (
    <div className="mlap-hg-remind" role="status">
      <svg className="mlap-hg-remind-ic" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h10M4 17h6" /></svg>
      <div className="mlap-hg-remind-tx"><b>{trFl('hg.remindT')}</b>{sub}{request.note ? <span className="mlap-hg-remind-note">{request.note}</span> : null}</div>
      {onCount ? <button type="button" className="mlap-hg-remind-go" onClick={onCount}>{trFl('hg.countNow')}</button> : null}
    </div>
  );
}

window.HG = Object.assign(window.HG || {}, { Count: HgCount, Saved: HgSaved, History: HgHistory, Card: HgCard, PhotoSheet: HgPhotoSheet, GalonKita: HgGalonKita, Remind: HgRemind });
